<template>
	<div class="bg5 p2">
		<div class="xs-12 flex wrap xs-between">
			<div class="fw7 fs4 c6 lh125">{{ productName }}</div>
		</div>
		<!-- header -->
		<div class="xs-12 mt6 flex">
			<div class="xs-6 fs3 fw7">{{ t("key") }}</div>
			<div class="xs-2 fs3 fw7">{{ t("rate") }} %</div>
			<div class="xs-2 fs3 fw7">{{ t("insured_amount") }}</div>
			<div class="xs-2 fs3 fw7 ta2">{{ t("premium") }}</div>
		</div>
		<!-- list -->
		<div class="mt2 mb6">
			<div
				v-for="(value, name) in values"
				:key="name"
				class="flex xs-12 pb1 xs-middle"
			>
				<div class="xs-6 fs3">{{ value.name }}</div>

				<!-- value -->
				<div class="xs-2 pr1">
					<core-form-input
						v-if="isEditable"
						componentName="j-base-input-number"
						:name="`${name}-value`"
						type="float"
						size="small"
						v-model="value.rate"
						v-validate="'required'"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="debouncedFetchPrice"
					/>
					<div v-if="!isEditable" class="fs3">{{ value.rate }}</div>
				</div>
				<!-- rate -->
				<div class="xs-2">
					<core-form-input
						v-if="isEditable"
						componentName="j-base-input-number"
						:name="`${name}-rate`"
						type="float"
						size="small"
						v-validate="'required'"
						v-model="value.value"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="debouncedFetchPrice"
					/>
					<div v-if="!isEditable" class="fs3">{{ value.value }}</div>
				</div>
				<div class="xs-2 flex xs-middle xs-center fs3">
					{{ parseFloat(((value.value * value.rate) / 100).toFixed(6)) }}
				</div>
			</div>
		</div>

		<div
			class="mb3 flex xs-around xs-middle"
			v-for="(item, i) in appliedTaxes"
			:key="i"
		>
			<div class="xs-6">
				<span class="fw7">{{ item.name }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fw7">{{ item.value }}</span>
			</div>
		</div>
		<div class="flex xs-around xs-middle">
			<div class="xs-6">
				<span class="fw7">{{ t("total_amount") }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fw7">{{ computedPrice }}</span>
			</div>
		</div>
		<!-- Save value -->
		<j-button v-if="isEditable" class="mt6" size="small" @click="saveValues">{{
			t("save_values")
		}}</j-button>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"

import JButton from "@/components/JButton"
import CoreFormInput from "@/modules/core/components/CoreFormInput"

const presetValues = {
	property: {
		rate: 0,
		value: 0
	}
}

export default {
	props: {
		productId: {
			validator(value) {
				return typeof value === "object"
			}
		},
		policyId: String,
		rawValues: Array,
		isEditable: {
			type: Boolean,
			default: false
		},
		endorsementId: {
			type: String,
			required: false,
			default: undefined
		}
	},

	components: {
		CoreFormInput,
		JButton
	},

	data() {
		return {
			productName: "",
			price: null,
			span: [],
			values: this.rawValues
				? this.massageValuesFromAPI(this.rawValues)
				: presetValues
		}
	},

	computed: {
		computedValues() {
			return this.values
		},
		computedPrice() {
			return this.price
		},
		computedTaxes() {
			return this.appliedTaxes
		},
		plan() {
			return this.productId?.plan
		}
	},

	methods: {
		t(key) {
			const locale = {
				"en-US": {
					key: "Key",
					rate: "Rate",
					premium: " Premium",
					save_values: "Save Values",
					total_amount: "Total Amount",
					retail: "Retail",
					wholesale: "Wholesale",
					value: "Value",
					insured_amount: "Insured Amount"
				},
				"zh-HK": {}
			}
			return locale[this.$store.state.settings.locale][key]
		},

		async fetchPrice() {
			const isValid = await this.$validator.validate()
			if (!isValid) return

			const query = gql`
				query getPrice($where: productWhereInput, $values: [keyValueInput]) {
					products: products_2(where: $where, values: $values) {
						list {
							name
							productId {
								plan
							}
							pricing(values: $values) {
								appliedTaxes {
									name
									code
									ratio
									formattedFlat
								}
								formattedPrice
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					where: {
						productId: {
							plan: this.plan
						}
					},
					...this.massageValuesForAPI()
				}
			})

			this.productName = res?.data?.products?.list?.[0]?.name
			this.price = res?.data?.products?.list?.[0]?.pricing?.formattedPrice
			this.appliedTaxes = res?.data?.products?.list?.[0]?.pricing?.appliedTaxes.map(
				item => {
					return {
						name: item.name ? item.name : item.code,
						value: this.getValue(item).flat
					}
				}
			)
		},

		debouncedFetchPrice: debounce(function() {
			this.fetchPrice()
		}, 500),

		async saveValues() {
			// Validate
			const isValid = await this.$validator.validate()
			if (!isValid) {
				this.$store.dispatch("showError", [
					"There are some missing or incorrect fields in the form"
				])
				return
			}

			const query = gql`
				mutation updatePolicy2(
					$input: updatePolicyInput!
					$endorsementId: String
					$policyId: String!
				) {
					updatePolicy2(
						policyId: $policyId
						input: $input
						endorsementId: $endorsementId
					) {
						errors
						status
					}
				}
			`
			const variables = {
				policyId: this.policyId,
				endorsementId: this.endorsementId,
				input: this.massageValuesForAPI()
			}

			const res = await api({
				query,
				variables
			})

			if (!res?.data?.updatePolicy2?.errors)
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.t("values_saved")
					}
				})
			this.$emit("pricing:updated")
		},

		massageValuesFromAPI(values) {
			const newValues = {}
			values
				?.find(item => item.key === "insureds")
				?.value?.arrayValue.forEach((item, i) => {
					let key = item?.objectValue[0]?.key // tour
					let value = item?.objectValue[0]?.value?.objectValue // array for 1 insured
					newValues[i] = {
						rate: parseFloat(
							(
								value.find(v => v.key === "rate")?.value?.numberValue * 100
							).toFixed(6)
						),
						name: value.find(v => v.key === "name")?.value?.stringValue,
						id: value.find(v => v.key === "id")?.value?.stringValue,
						value: value.find(v => v.key === "value")?.value?.numberValue
					}
				})
			return newValues
		},

		massageValuesForAPI() {
			return {
				values: [
					{
						key: "insureds",
						value: {
							arrayValue: Object.values(this.computedValues).map(
								computedValue => {
									return {
										objectValue: [
											{
												key: "exhibition",
												value: {
													objectValue: [
														{
															key: "rate",
															value: {
																numberValue: computedValue.rate / 100
															}
														},
														{
															key: "value",
															value: {
																numberValue: computedValue.value
															}
														},
														{
															key: "name",
															value: {
																stringValue: computedValue.name
															}
														}
													]
												}
											}
										]
									}
								}
							)
						}
					}
				]
			}
		},

		getValue(item) {
			if (!item) return ""
			const ratio = item.ratio ? item.ratio * 100 : null
			const flat = item.formattedFlat ? item.formattedFlat : "0"
			return {
				flat,
				summary: ratio
					? `(${ratio ? ratio.toFixed(2) : "0"}% of ${
							item.formattedOriginalPrice
					  })`
					: ""
			}
		}
	},

	async created() {
		await this.fetchPrice()
	}
}
</script>
